import { LEVELS, type LevelDef } from "../game/levels.ts";
import { GameLoop } from "../game/loop.ts";
import type { GameContext, GameEvent } from "../game/state.ts";
import { Renderer } from "../render/canvas.ts";
import { GeneratorPanel } from "./controls.ts";

type View = "menu" | "briefing" | "playing" | "won" | "lost";

export class App {
  private root: HTMLElement;
  private hud!: HTMLElement;
  private sidebar!: HTMLElement;
  private viewport!: HTMLElement;
  private canvas!: HTMLCanvasElement;
  private overlay: HTMLElement | null = null;
  private toast: HTMLElement | null = null;
  private progressFill: HTMLElement | null = null;

  private renderer!: Renderer;
  private loop!: GameLoop;
  private panel!: GeneratorPanel;

  private currentLevel: LevelDef | null = null;
  private ctx: GameContext | null = null;
  private view: View = "menu";
  // Track which events have already been toasted
  private toastedEvents = new WeakSet<GameEvent>();

  constructor(root: HTMLElement) {
    this.root = root;
    this.buildShell();
    this.showMenu();
    this.bindKeys();
  }

  private buildShell(): void {
    this.root.innerHTML = "";

    this.hud = document.createElement("div");
    this.hud.className = "hud";
    this.root.appendChild(this.hud);
    this.renderHUD();

    const main = document.createElement("div");
    main.className = "main";
    this.root.appendChild(main);

    this.sidebar = document.createElement("div");
    this.sidebar.className = "sidebar";
    main.appendChild(this.sidebar);

    this.viewport = document.createElement("div");
    this.viewport.className = "viewport";
    main.appendChild(this.viewport);

    this.canvas = document.createElement("canvas");
    this.viewport.appendChild(this.canvas);

    this.toast = document.createElement("div");
    this.toast.className = "event-toast";
    this.viewport.appendChild(this.toast);

    const bar = document.createElement("div");
    bar.className = "progress-bar";
    const fill = document.createElement("div");
    fill.className = "fill";
    bar.appendChild(fill);
    this.viewport.appendChild(bar);
    this.progressFill = fill;

    this.renderer = new Renderer(this.canvas);
    this.panel = new GeneratorPanel(this.sidebar, {
      onSetpoint: (id, mw) => this.setSetpoint(id, mw),
      onToggle: (id) => this.toggleGenerator(id),
      showCost: false,
    });

    // Loop is created once with a dummy context; replaced on level start
    this.loop = new GameLoop({
      state: { gameMinutes: 0, frequency: 50, supplyMW: 0, demandMW: 0, generators: [], loads: [], cumulativeCost: 0, status: "playing", winProgressSeconds: 0 },
      constants: { nominalFrequency: 50, tripLow: 48.5, tripHigh: 51.5, bandLow: 49.8, bandHigh: 50.2, inertiaConstant: 6, damping: 0.8, systemBase: 1000 },
      winSeconds: 1,
      events: [],
    });
    this.loop.onUpdate((c) => this.onTick(c));
    this.loop.onFrame((c) => this.onFrame(c));
    this.loop.start();
  }

  private renderHUD(): void {
    this.hud.innerHTML = "";
    const brand = document.createElement("div");
    brand.className = "brand";
    brand.innerHTML = `<span class="dot">◆</span> GRIDKEEPER`;
    this.hud.appendChild(brand);

    if (this.currentLevel && this.view === "playing") {
      this.hud.appendChild(this.stat("FREQ", "—", "freq"));
      this.hud.appendChild(this.stat("SUPPLY", "—", "supply"));
      this.hud.appendChild(this.stat("DEMAND", "—", "demand"));
      this.hud.appendChild(this.stat("IMBAL", "—", "imbal"));
      if (this.ctx?.budgetUSD !== undefined) {
        this.hud.appendChild(this.stat("COST", "—", "cost"));
      }
      if (this.ctx?.state.revenue !== undefined) {
        this.hud.appendChild(this.stat("MARGIN", "—", "margin"));
      }
      if (this.ctx?.restorationTarget !== undefined) {
        this.hud.appendChild(this.stat("RESTORED", "—", "restored"));
      }
      this.hud.appendChild(this.stat("TIME", "—", "time"));
    }

    const spacer = document.createElement("div");
    spacer.className = "spacer";
    this.hud.appendChild(spacer);

    if (this.view === "playing") {
      const pauseBtn = document.createElement("button");
      pauseBtn.className = "btn";
      pauseBtn.textContent = this.loop?.isPaused() ? "Resume (Space)" : "Pause (Space)";
      pauseBtn.addEventListener("click", () => {
        this.loop.togglePause();
        this.renderHUD();
      });
      this.hud.appendChild(pauseBtn);

      for (const speed of [1, 2, 4]) {
        const btn = document.createElement("button");
        btn.className = "btn" + (this.loop?.getSpeed() === speed ? " active" : "");
        btn.textContent = `${speed}×`;
        btn.addEventListener("click", () => {
          this.loop.setSpeed(speed);
          this.renderHUD();
        });
        this.hud.appendChild(btn);
      }

      const menuBtn = document.createElement("button");
      menuBtn.className = "btn";
      menuBtn.textContent = "Menu";
      menuBtn.addEventListener("click", () => this.showMenu());
      this.hud.appendChild(menuBtn);
    }
  }

  private stat(label: string, value: string, cls: string): HTMLElement {
    const wrap = document.createElement("div");
    wrap.className = `stat ${cls}`;
    wrap.innerHTML = `<div class="label">${label}</div><div class="value">${value}</div>`;
    return wrap;
  }

  // --- Menu ---

  private showMenu(): void {
    this.view = "menu";
    this.currentLevel = null;
    this.sidebar.innerHTML = "";
    this.clearOverlay();
    this.renderHUD();

    const overlay = document.createElement("div");
    overlay.className = "overlay";
    const card = document.createElement("div");
    card.className = "card";
    card.innerHTML = `
      <h1>Gridkeeper</h1>
      <div class="sub">A nine-layer introduction to how power grids actually work</div>
      <p style="font-size:13px;color:var(--dim);line-height:1.6;margin:0 0 8px">
        You are the operator. Match supply to demand in real time and keep the lights on.
        Each layer adds one new concept; the earlier ones still apply.
      </p>
    `;
    const grid = document.createElement("div");
    grid.className = "menu-grid";
    for (const lv of LEVELS) {
      const b = document.createElement("button");
      b.className = "menu-card" + (lv.available ? "" : " locked");
      b.disabled = !lv.available;
      b.innerHTML = `
        <div class="layer-num">Layer ${lv.layer}</div>
        <div class="layer-title">${lv.title}</div>
        <div class="layer-sub">${lv.subtitle.split("·")[1]?.trim() ?? lv.subtitle}</div>
        ${lv.available ? "" : '<div class="lock">Coming soon</div>'}
      `;
      b.addEventListener("click", () => this.startLevel(lv));
      grid.appendChild(b);
    }
    card.appendChild(grid);
    overlay.appendChild(card);
    this.viewport.appendChild(overlay);
    this.overlay = overlay;
  }

  private startLevel(lv: LevelDef): void {
    this.currentLevel = lv;
    this.ctx = lv.build();
    this.loop.setContext(this.ctx);
    this.loop.setSpeed(1);
    if (!this.loop.isPaused()) this.loop.togglePause();
    this.renderer.setLevel(lv.id);
    this.sidebar.innerHTML = "";
    const h = document.createElement("h2");
    h.textContent = "Generators";
    this.sidebar.appendChild(h);
    this.panel = new GeneratorPanel(this.sidebar, {
      onSetpoint: (id, mw) => this.setSetpoint(id, mw),
      onToggle: (id) => this.toggleGenerator(id),
      showCost: this.ctx.budgetUSD !== undefined,
    });
    this.panel.setGenerators(this.ctx.state.generators);
    this.toastedEvents = new WeakSet();

    this.showBriefing(lv);
  }

  private showBriefing(lv: LevelDef): void {
    this.view = "briefing";
    this.clearOverlay();
    this.renderHUD();

    const overlay = document.createElement("div");
    overlay.className = "overlay";
    const card = document.createElement("div");
    card.className = "card";
    card.innerHTML = `
      <h1>${lv.title}</h1>
      <div class="sub">${lv.subtitle}</div>
      <ul>
        ${lv.briefing.map((b) => `<li>${b}</li>`).join("")}
      </ul>
    `;
    const actions = document.createElement("div");
    actions.className = "actions";
    const back = document.createElement("button");
    back.textContent = "Back to menu";
    back.addEventListener("click", () => this.showMenu());
    const play = document.createElement("button");
    play.className = "primary";
    play.textContent = "Begin";
    play.addEventListener("click", () => this.beginPlay());
    actions.appendChild(back);
    actions.appendChild(play);
    card.appendChild(actions);
    overlay.appendChild(card);
    this.viewport.appendChild(overlay);
    this.overlay = overlay;
  }

  private beginPlay(): void {
    this.view = "playing";
    this.clearOverlay();
    if (this.loop.isPaused()) this.loop.togglePause();
    this.renderHUD();
  }

  // --- Controls ---

  private setSetpoint(id: string, mw: number): void {
    const g = this.ctx?.state.generators.find((x) => x.id === id);
    if (!g) return;
    if (g.status === "on") g.setpoint = mw;
  }

  private toggleGenerator(id: string): void {
    const g = this.ctx?.state.generators.find((x) => x.id === id);
    if (!g) return;
    if (g.status === "off") {
      g.status = "starting";
      g.statusTimer = g.startupTime;
      g.setpoint = g.pMin;
    } else if (g.status === "on") {
      g.status = "stopping";
      g.statusTimer = 5;
    }
    // starting/stopping/tripped: ignore
  }

  // --- Tick ---

  private onTick(ctx: GameContext): void {
    // Show toast for newly-fired events
    for (const ev of ctx.events) {
      if (ev.fired && !this.toastedEvents.has(ev)) {
        this.toastedEvents.add(ev);
        this.showToast(ev.label);
      }
    }
    this.panel.update(ctx.state.generators);

    // Win/loss → overlay
    if (ctx.state.status === "won" && this.view === "playing") {
      this.view = "won";
      this.loop.togglePause();
      this.showOutcome("Level Complete", `Held the band for ${ctx.winSeconds.toFixed(0)}s. Cost: $${ctx.state.cumulativeCost.toFixed(0)}.`, true);
    } else if (ctx.state.status === "lost" && this.view === "playing") {
      this.view = "lost";
      this.loop.togglePause();
      this.showOutcome("Blackout", ctx.state.loseReason ?? "Grid failed.", false);
    }
  }

  private onFrame(ctx: GameContext): void {
    this.renderer.render(ctx.state, ctx.constants);
    if (this.view === "playing") this.updateHUDValues(ctx);
    if (this.progressFill) {
      const pct = ctx.winSeconds > 0 ? Math.min(100, (ctx.state.winProgressSeconds / ctx.winSeconds) * 100) : 0;
      this.progressFill.style.width = `${pct}%`;
    }
  }

  private updateHUDValues(ctx: GameContext): void {
    const s = ctx.state;
    const setStat = (cls: string, value: string, extraClass = "") => {
      const el = this.hud.querySelector(`.stat.${cls}`);
      if (el) {
        el.className = `stat ${cls} ${extraClass}`;
        const v = el.querySelector(".value");
        if (v) v.textContent = value;
      }
    };
    const c = ctx.constants;
    const fclass = s.frequency < c.tripLow || s.frequency > c.tripHigh ? "danger"
      : s.frequency < c.bandLow || s.frequency > c.bandHigh ? "warn"
        : "good";
    setStat("freq", `${s.frequency.toFixed(2)} Hz`, fclass);
    setStat("supply", `${Math.round(s.supplyMW)} MW`);
    setStat("demand", `${Math.round(s.demandMW)} MW`);
    const dP = s.supplyMW - s.demandMW;
    setStat("imbal", `${dP >= 0 ? "+" : ""}${Math.round(dP)} MW`);
    if (ctx.budgetUSD !== undefined) {
      setStat("cost", `$${Math.round(s.cumulativeCost)} / ${Math.round(ctx.budgetUSD)}`);
    }
    if (s.revenue !== undefined) {
      setStat("margin", `$${Math.round(s.revenue)}`);
    }
    if (ctx.restorationTarget !== undefined && s.restorationProgress !== undefined) {
      const pct = Math.round(s.restorationProgress * 100);
      const target = Math.round(ctx.restorationTarget * 100);
      setStat("restored", `${pct}% / ${target}%`);
    }
    setStat("time", `${formatMinutes(s.gameMinutes)}`);
  }

  // --- Overlays ---

  private showToast(text: string): void {
    if (!this.toast) return;
    this.toast.textContent = text;
    this.toast.classList.add("show");
    window.setTimeout(() => this.toast?.classList.remove("show"), 2500);
  }

  private showOutcome(title: string, body: string, won: boolean): void {
    this.clearOverlay();
    const overlay = document.createElement("div");
    overlay.className = "overlay";
    const card = document.createElement("div");
    card.className = "card";
    card.innerHTML = `
      <h1>${title}</h1>
      <div class="sub" style="color:${won ? "var(--good)" : "var(--danger)"}">${won ? "Operator skill: confirmed" : "Lessons learned"}</div>
      <p style="font-size:13px;line-height:1.6;">${body}</p>
    `;
    const actions = document.createElement("div");
    actions.className = "actions";
    const menu = document.createElement("button");
    menu.textContent = "Menu";
    menu.addEventListener("click", () => this.showMenu());
    const retry = document.createElement("button");
    retry.className = "primary";
    retry.textContent = won ? "Next layer" : "Retry";
    retry.addEventListener("click", () => {
      if (won) {
        const next = LEVELS.find((l) => l.id === (this.currentLevel?.id ?? 0) + 1);
        if (next && next.available) this.startLevel(next);
        else this.showMenu();
      } else if (this.currentLevel) {
        this.startLevel(this.currentLevel);
      }
    });
    actions.appendChild(menu);
    actions.appendChild(retry);
    card.appendChild(actions);
    overlay.appendChild(card);
    this.viewport.appendChild(overlay);
    this.overlay = overlay;
  }

  private clearOverlay(): void {
    if (this.overlay) {
      this.overlay.remove();
      this.overlay = null;
    }
  }

  private bindKeys(): void {
    window.addEventListener("keydown", (e) => {
      if (e.code === "Space" && this.view === "playing") {
        e.preventDefault();
        this.loop.togglePause();
        this.renderHUD();
      } else if (e.code === "Escape" && (this.view === "playing" || this.view === "briefing")) {
        this.showMenu();
      } else if (e.code === "Digit1" && this.view === "playing") this.setSpeedFromKey(1);
      else if (e.code === "Digit2" && this.view === "playing") this.setSpeedFromKey(2);
      else if (e.code === "Digit4" && this.view === "playing") this.setSpeedFromKey(4);
    });
  }

  private setSpeedFromKey(s: number): void {
    this.loop.setSpeed(s);
    this.renderHUD();
  }
}

function formatMinutes(m: number): string {
  const mm = Math.floor(m);
  const ss = Math.floor((m - mm) * 60);
  return `${mm}m${ss.toString().padStart(2, "0")}s`;
}
